import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, api, keys, type Channel, type EventRecurrence, type ServerEvent, type Uuid } from "@shared/api";
import { Dialog, Field } from "@shared/ui/bits";
import { FileImage } from "@shared/ui/FileImage";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Textarea } from "@shared/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { IconPlace, IconVoiceChannel } from "@shared/ui/icons";
import { fromLocal, isLive, lengthOf, nextHour, toLocal, whenLabel } from "../lib/when";
import { isCall } from "@entities/channel";


/** «Не повторяется» в списке: пустое значение Radix не принимает. */
const NO_REPEAT = "none";
/** Повтор — как «Частота» в Discord. */
const REPEATS: [EventRecurrence, string][] = [
  ["weekly", "Каждую неделю"],
  ["biweekly", "Раз в две недели"],
  ["monthly", "Каждый месяц"],
  ["yearly", "Каждый год"],
  ["weekdays", "По будням"],
];

const NAME_MAX = 100;
const DESCRIPTION_MAX = 1000;
const LOCATION_MAX = 100;

/** Длительность — как в форме Discord, только списком: конец можно и не назначать. */
const LENGTHS: { hours: number; label: string }[] = [
  { hours: 0, label: "Без конца" },
  { hours: 1, label: "1 час" },
  { hours: 2, label: "2 часа" },
  { hours: 3, label: "3 часа" },
  { hours: 6, label: "6 часов" },
  { hours: 24, label: "Весь день" },
];

const ELSEWHERE = "elsewhere";

/** Окно событий сервера: список с откликом «интересно» и форма нового. */
export function EventsDialog({
  serverId,
  events,
  channels,
  meId,
  canCreate,
  canManageAll,
  onJoin,
  onClose,
}: {
  serverId: Uuid;
  events: ServerEvent[];
  channels: Channel[];
  meId: Uuid;
  /** Заводить события — правом «создавать события» или «управлять событиями». */
  canCreate: boolean;
  /** Отменять любые; свои — и с одним правом «создавать». */
  canManageAll: boolean;
  onJoin: (channel: Channel) => void;
  onClose: () => void;
}) {
  // Форма поверх списка: `true` — новое событие, само событие — его правка.
  const [form, setForm] = useState<ServerEvent | true | null>(null);

  if (form) {
    return (
      <EventForm
        serverId={serverId}
        editing={form === true ? null : form}
        voice={channels.filter((channel) => isCall(channel.channel_type))}
        onDone={() => setForm(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <Dialog
      title="События"
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
          {canCreate && (
            <Button type="button" onClick={() => setForm(true)}>
              Создать событие
            </Button>
          )}
        </>
      }
    >
      {events.length === 0 ? (
        <div className="events-empty small muted">
          Впереди ничего нет. Назначьте встречу — участники увидят её над каналами и смогут откликнуться.
        </div>
      ) : (
        <div className="events-list">
          {events.map((event) => (
            <EventCard
              key={event.id}
              serverId={serverId}
              event={event}
              channel={channels.find((channel) => channel.id === event.channel_id) ?? null}
              canCancel={canManageAll || (canCreate && event.creator_id === meId)}
              onJoin={onJoin}
              onEdit={() => setForm(event)}
            />
          ))}
        </div>
      )}
    </Dialog>
  );
}

function EventCard({
  serverId,
  event,
  channel,
  canCancel,
  onJoin,
  onEdit,
}: {
  serverId: Uuid;
  event: ServerEvent;
  channel: Channel | null;
  canCancel: boolean;
  onJoin: (channel: Channel) => void;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const live = isLive(event);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: keys.events(serverId) });
    }
  }

  return (
    <article className={`event-card${live ? " live" : ""}`}>
      {event.cover_id && <FileImage id={event.cover_id} alt="" className="event-cover" />}
      <div className="event-when small">
        {live ? <span className="event-live">Идёт сейчас</span> : whenLabel(event.starts)}
        {event.ends && <span className="muted"> — до {whenLabel(event.ends).split(", ").pop()}</span>}
      </div>
      <div className="event-name">{event.name}</div>
      {event.recurrence && (
        <div className="event-repeat small muted">
          Повторяется: {REPEATS.find(([value]) => value === event.recurrence)?.[1].toLowerCase()}
        </div>
      )}
      {event.description && <div className="event-description small">{event.description}</div>}
      <div className="event-place small muted">
        {channel ? <IconVoiceChannel size={14} /> : <IconPlace size={14} />}
        <span className="ellipsis">{channel ? channel.name : (event.location ?? "Канал недоступен")}</span>
      </div>
      <div className="event-actions">
        <Button
          type="button"
          size="sm"
          variant={event.mine ? "default" : "secondary"}
          aria-pressed={event.mine}
          disabled={busy}
          onClick={() => void run(() => api.events.interest(serverId, event.id, !event.mine))}
        >
          {event.mine ? "✓ Интересно" : "Интересно"}
          {event.interested > 0 && <span className="event-count">{event.interested}</span>}
        </Button>
        {live && channel && (
          <Button type="button" size="sm" onClick={() => onJoin(channel)}>
            Присоединиться
          </Button>
        )}
        {/* «Начать» раньше срока и «Завершить» идущее — как в Discord, тем, кто правит событие. */}
        {canCancel && (
          <Button
            type="button"
            size="sm"
            variant={live ? "secondary" : "default"}
            disabled={busy}
            onClick={() =>
              void run(() => (live ? api.events.end(serverId, event.id) : api.events.start(serverId, event.id)))
            }
          >
            {live ? "Завершить" : "Начать"}
          </Button>
        )}
        {canCancel && (
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onEdit}>
            Изменить
          </Button>
        )}
        {canCancel && (
          <Button
            type="button"
            size="sm"
            variant={confirming ? "danger" : "ghost"}
            disabled={busy}
            onBlur={() => setConfirming(false)}
            onClick={() =>
              confirming ? void run(() => api.events.remove(serverId, event.id)) : setConfirming(true)
            }
          >
            {confirming ? "Точно отменить?" : "Отменить"}
          </Button>
        )}
      </div>
    </article>
  );
}

/**
 * «Создать событие» из меню сервера — как в Discord: сразу форма, без списка.
 * Создали или отменили — окно закрывается.
 */
export function NewEventDialog({
  serverId,
  channels,
  onClose,
}: {
  serverId: Uuid;
  channels: Channel[];
  onClose: () => void;
}) {
  return (
    <EventForm
      serverId={serverId}
      editing={null}
      voice={channels.filter((channel) => isCall(channel.channel_type))}
      back="Отмена"
      onDone={onClose}
      onClose={onClose}
    />
  );
}

function EventForm({
  serverId,
  editing,
  voice,
  back = "Назад",
  onDone,
  onClose,
}: {
  serverId: Uuid;
  /** Правимое событие; `null` — новое. */
  editing: ServerEvent | null;
  voice: Channel[];
  /** Подпись кнопки возврата: из списка — «Назад», из меню сервера — «Отмена». */
  back?: string;
  onDone: () => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const start = editing ? toLocal(editing.starts) : nextHour();
  const [name, setName] = useState(editing?.name ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [place, setPlace] = useState<string>(
    editing ? (editing.channel_id ?? ELSEWHERE) : (voice[0]?.id ?? ELSEWHERE),
  );
  const [location, setLocation] = useState(editing?.location ?? "");
  const [cover, setCover] = useState<Uuid | null>(editing?.cover_id ?? null);
  const [repeat, setRepeat] = useState<string>(editing?.recurrence ?? NO_REPEAT);
  const picker = useRef<HTMLInputElement>(null);
  const [date, setDate] = useState(start.date);
  const [clock, setClock] = useState(start.clock);
  const [hours, setHours] = useState(editing ? lengthOf(editing) : 0);
  // Длительность не из списка (заведена через API) — своим пунктом, чтобы правка её не сбросила.
  const lengths = LENGTHS.some((item) => item.hours === hours)
    ? LENGTHS
    : [...LENGTHS, { hours, label: `${hours} ч` }];
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Время не трогали — уходит прежним до секунды: идущее событие сервер иначе
  // счёл бы назначенным в прошлое, а конец «как был» не округлится до часа.
  const untouched = !!editing && date === start.date && clock === start.clock;
  const starts = untouched ? editing.starts : fromLocal(date, clock);
  const elsewhere = place === ELSEWHERE;
  const ready = name.trim().length > 0 && !!starts && (!elsewhere || location.trim().length > 0);

  async function save() {
    if (!starts) return;
    setBusy(true);
    setProblem(null);
    const draft = {
      name: name.trim(),
      description: description.trim() || undefined,
      starts,
      ends:
        untouched && hours === lengthOf(editing)
          ? (editing.ends ?? undefined)
          : hours > 0
            ? new Date(Date.parse(starts) + hours * 3_600_000).toISOString()
            : undefined,
      channel_id: elsewhere ? undefined : place,
      location: elsewhere ? location.trim() : undefined,
      cover_id: cover,
      recurrence: repeat === NO_REPEAT ? null : (repeat as EventRecurrence),
    };
    try {
      if (editing) await api.events.update(serverId, editing.id, draft);
      else await api.events.create(serverId, draft);
      await queryClient.invalidateQueries({ queryKey: keys.events(serverId) });
      onDone();
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.code === "automod"
          ? "АвтоМод сервера не пропустил слово"
          : error instanceof ApiError && error.code === "event_time"
          ? "Начало — в будущем, не дальше года; конец — позже начала"
          : error instanceof ApiError
            ? error.message
            : editing
              ? "Событие не сохранилось"
              : "Событие не создалось",
      );
    } finally {
      setBusy(false);
    }
  }

  /** Картинка грузится сразу, а к событию привяжется при сохранении. */
  async function pickCover(file: File) {
    setBusy(true);
    setProblem(null);
    try {
      setCover((await api.files.upload(file, "icon")).id);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Обложка не загрузилась");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={editing ? "Изменить событие" : "Создать событие"}
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onDone}>
            {back}
          </Button>
          <Button type="button" disabled={!ready || busy} onClick={() => void save()}>
            {editing ? "Сохранить" : "Создать"}
          </Button>
        </>
      }
    >
      <div className="event-form">
        <Field label="Где">
          <Select value={place} onValueChange={setPlace}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {voice.map((channel) => (
                <SelectItem key={channel.id} value={channel.id}>
                  🔊 {channel.name}
                </SelectItem>
              ))}
              <SelectItem value={ELSEWHERE}>Где-то ещё</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {elsewhere && (
          <Field label="Место">
            <Input
              value={location}
              maxLength={LOCATION_MAX}
              placeholder="Адрес или ссылка"
              onChange={(event) => setLocation(event.target.value)}
            />
          </Field>
        )}
        <Field label="Название">
          <Input
            value={name}
            maxLength={NAME_MAX}
            placeholder="Что будет?"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <div className="event-form-time">
          <Field label="Дата">
            <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </Field>
          <Field label="Время">
            <Input type="time" value={clock} onChange={(event) => setClock(event.target.value)} />
          </Field>
          <Field label="Длительность">
            <Select value={String(hours)} onValueChange={(value) => setHours(Number(value))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {lengths.map((item) => (
                  <SelectItem key={item.hours} value={String(item.hours)}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Повтор">
          <Select value={repeat} onValueChange={setRepeat}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_REPEAT}>Не повторяется</SelectItem>
              {REPEATS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Описание">
          <Textarea
            value={description}
            maxLength={DESCRIPTION_MAX}
            rows={3}
            placeholder="Необязательно"
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <Field label="Обложка">
          <div className="line" style={{ gap: 8, alignItems: "center" }}>
            {cover && <FileImage id={cover} alt="" className="event-cover-thumb" />}
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => picker.current?.click()}>
              {cover ? "Сменить" : "Добавить картинку"}
            </Button>
            {cover && (
              <Button type="button" variant="danger" size="sm" disabled={busy} onClick={() => setCover(null)}>
                Убрать
              </Button>
            )}
            <input
              ref={picker}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void pickCover(file);
              }}
            />
          </div>
        </Field>
        {problem && <div className="small danger-text">{problem}</div>}
      </div>
    </Dialog>
  );
}
